import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import CampaignList from './components/CampaignList';
import CampaignDetail from './components/CampaignDetail';
import CreateCampaign from './components/CreateCampaign';
import AdminUsers from './components/AdminUsers';
import Login from './shared/components/Login';
import ToastContainer from './shared/components/ToastContainer';
import { ToastProvider, useToastContext } from './shared/context/ToastContext';
import { AuthProvider, useAuth } from './shared/context/AuthContext';

const APP = {
  label: 'XML Catalogue',
  tagline: 'Manage campaigns and generate catalog feeds',
  title: 'XML Catalogue Manager',
};

const NAV_ITEMS = [
  { label: 'Campaigns', path: '/' },
  { label: 'Create Campaign', path: '/campaigns/new' },
];

function ThemeToggle() {
  const [theme, setTheme] = React.useState(() => {
    const stored = localStorage.getItem('theme');
    return stored === 'light' ? 'light' : 'dark';
  });

  React.useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <button
      className="btn btn-ghost btn-sm"
      onClick={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
      aria-label="Toggle dark mode"
      title="Toggle dark mode"
      type="button"
    >
      {theme === 'dark' ? 'Light' : 'Dark'}
    </button>
  );
}

function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    document.documentElement.classList.add('dark');
    const stored = localStorage.getItem('theme');
    if (stored === 'light') {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const adminItem = isAdmin ? { label: 'Admin', path: '/admin/users' } : null;
  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand" onClick={() => navigate('/')}>
          <h1>{APP.label}</h1>
          <p>Manager</p>
        </div>
        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.path}
              className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
              type="button"
            >
              {item.label}
            </button>
          ))}
          {adminItem && (
            <button
              className={`sidebar-item sidebar-item-admin ${isActive(adminItem.path) ? 'active' : ''}`}
              onClick={() => navigate(adminItem.path)}
              type="button"
            >
              {adminItem.label}
            </button>
          )}
        </nav>
      </aside>

      <div className="app-content">
        <header className="App-header">
          <div className="header-content">
            <button
              className="btn btn-ghost btn-sm mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              type="button"
              aria-label="Open navigation menu"
            >
              Menu
            </button>
            <div className="header-logo">
              <h2>{APP.title}</h2>
              <p>{APP.tagline}</p>
            </div>
            {user && (
              <div className="header-user">
                <span>{user.email}</span>
                <ThemeToggle />
                <button onClick={logout} className="btn btn-secondary btn-sm" type="button">
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="App-main">{children}</main>
      </div>

      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />}
      <aside className={`mobile-drawer ${mobileOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <h3>Navigation</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => setMobileOpen(false)} type="button">
            Close
          </button>
        </div>
        <div className="mobile-drawer-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={`mobile-${item.path}`}
              className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              type="button"
            >
              {item.label}
            </button>
          ))}
          {adminItem && (
            <button
              className={`sidebar-item ${isActive(adminItem.path) ? 'active' : ''}`}
              onClick={() => {
                navigate(adminItem.path);
                setMobileOpen(false);
              }}
              type="button"
            >
              {adminItem.label}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { loading, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();
  if (loading) return <div className="loading">Loading session...</div>;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppContent() {
  const { toasts, removeToast } = useToastContext();
  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  }, []);

  return (
    <>
      {isAuthenticated ? (
        <AppShell>
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/" element={<ProtectedRoute><CampaignList /></ProtectedRoute>} />
            <Route path="/campaigns/new" element={<ProtectedRoute><CreateCampaign /></ProtectedRoute>} />
            <Route path="/campaigns/:id" element={<ProtectedRoute><CampaignDetail /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
          </Routes>
        </AppShell>
      ) : (
        <main className="App-main">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </main>
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
}

function App() {
  React.useEffect(() => {
    document.title = APP.title;
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <div className="App">
            <AppContent />
          </div>
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
